import React, { useState, useEffect, useCallback } from 'react'
import { Search, RefreshCw, TrendingUp, TrendingDown, Filter, BarChart2, AlertCircle, Clock } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { api } from '../lib/api'
import { fmtPrice, fmtSignalType, timeAgo, strengthColor } from '../lib/utils'
import { useSettings } from '../context/SettingsContext'
import TVChart from '../components/TVChart'

// Signal Optimizer panel removed 2026-08-11 -- confirmed non-functional for 4+ months (read an empty config stub, its button called a script filename that didn't exist, and even fixed, that script never persisted results anywhere). See docs/SAFERFUNDS_DATA_ERROR_FORENSICS_2026_08_11.md.
// ── Dark custom tooltip for Recharts ──────────────────────────────────────────
const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg px-3 py-2 text-xs border" style={{ background: '#0d1423', borderColor: '#1f2937' }}>
      <p className="text-slate-500 mb-1" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || '#94a3b8', fontFamily: 'JetBrains Mono, monospace' }}>
          Count: {p.value}
        </p>
      ))}
    </div>
  )
}

// ── Signal breakdown bar chart ─────────────────────────────────────────────────
function SignalTypeBreakdown({ data }) {
  if (!data.length) return null
  const colors = { buy: '#10b981', sell: '#f43f5e' }
  return (
    <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
      <h3 className="section-header">Signal Breakdown (24h)</h3>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ left: -10 }}>
            <CartesianGrid strokeDasharray="2 5" stroke="#1e293b" />
            <XAxis
              dataKey="signal_type"
              tick={{ fontSize: 8, fill: '#475569', fontFamily: 'JetBrains Mono, monospace' }}
              stroke="#1e293b"
              tickFormatter={v => v.replace(/_/g, '·').slice(0, 12)}
            />
            <YAxis
              tick={{ fontSize: 9, fill: '#475569', fontFamily: 'JetBrains Mono, monospace' }}
              stroke="#1e293b"
            />
            <Tooltip content={<DarkTooltip />} />
            <Bar dataKey="count" radius={[3, 3, 0, 0]} name="Count">
              {data.map((entry, i) => (
                <Cell key={i} fill={colors[entry.direction] || '#6366f1'} fillOpacity={0.85} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex gap-4 mt-3 text-xs text-slate-600">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#10b981' }} />
          BUY
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: '#f43f5e' }} />
          SELL
        </span>
      </div>
    </div>
  )
}

// ── Top symbols list ───────────────────────────────────────────────────────────
function TopSymbols({ data }) {
  if (!data.length) return null
  const max = data[0]?.signal_count || 1
  return (
    <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
      <h3 className="section-header">Top Symbols (24h)</h3>
      <div className="space-y-2.5">
        {data.map((row, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="text-slate-700 w-4 text-right tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem' }}>
              {i + 1}
            </span>
            <span className="font-semibold text-slate-300 w-14 text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {row.symbol}
            </span>
            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: '#1e293b' }}>
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(100, (row.signal_count / max) * 100)}%`,
                  background: 'linear-gradient(90deg, #1d4ed8, #3b82f6)',
                }}
              />
            </div>
            <span className="text-slate-500 w-8 text-right tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>
              {row.signal_count}
            </span>
            <span className="text-emerald-500 w-6 text-right" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>
              {row.buys}↑
            </span>
            <span className="text-rose-500 w-6 text-right" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>
              {row.sells}↓
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Dark select helper ─────────────────────────────────────────────────────────
const darkSelect = {
  background: '#111827',
  border: '1px solid #374151',
  borderRadius: '0.5rem',
  color: '#94a3b8',
  padding: '0.375rem 0.75rem',
  fontSize: '0.75rem',
  fontFamily: 'JetBrains Mono, monospace',
  letterSpacing: '0.04em',
  outline: 'none',
  cursor: 'pointer',
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function Analysis() {
  const { settings, passesFilter } = useSettings()
  const [signals,    setSignals]    = useState([])
  const [breakdown,  setBreakdown]  = useState([])
  const [topSyms,    setTopSyms]    = useState([])
  const [loading,    setLoading]    = useState(true)
  const [error,      setError]      = useState(null)
  const [symbol,     setSymbol]     = useState(settings.defaultSymbol || 'SPY')
  const [symBars,    setSymBars]    = useState([])
  const [symLoading, setSymLoading] = useState(false)
  const [localDir,   setLocalDir]   = useState('ALL')
  const [localHours, setLocalHours] = useState(24)

  const fetchSignals = useCallback(async () => {
    setLoading(true)
    try {
      const [sigRes, brkRes, topRes] = await Promise.allSettled([
        api.signals(localHours, 200),
        api.signalBreakdown(localHours),
        api.topSymbols(localHours, 15),
      ])

      if (sigRes.status === 'fulfilled' && sigRes.value.success) {
        const rows = (sigRes.value.data || []).map(r => ({
          time: r[0], symbol: r[1], signal_type: r[2], direction: r[3],
          strength: r[4], price: r[5], confidence: r[6]
        }))
        const filtered = rows.filter(s => {
          if (localDir !== 'ALL' && (s.direction||'').toUpperCase() !== localDir) return false
          return passesFilter(s)
        })
        setSignals(filtered)
      }

      if (brkRes.status === 'fulfilled' && brkRes.value.success) {
        const rows = (brkRes.value.data || []).map(r => ({
          signal_type: r[0], direction: r[1], count: Number(r[2]), avg_strength: Number(r[3])
        }))
        setBreakdown(rows)
      }

      if (topRes.status === 'fulfilled' && topRes.value.success) {
        const rows = (topRes.value.data || []).map(r => ({
          symbol: r[0], signal_count: Number(r[1]), buys: Number(r[2]), sells: Number(r[3]), avg_strength: Number(r[4])
        }))
        setTopSyms(rows)
      }

      setError(null)
    } catch (e) {
      setError('Failed to load signal data.')
    } finally {
      setLoading(false)
    }
  }, [localDir, localHours, passesFilter])

  useEffect(() => { fetchSignals() }, [fetchSignals])

  const lookupSymbol = async () => {
    if (!symbol.trim()) return
    setSymLoading(true)
    try {
      const res = await api.symbolBars(symbol.trim(), 60)
      if (res.success) {
        const rows = (res.data || []).map(r => ({
          time: r[0], close: r[1], rsi: r[2], supertrend: r[3],
          volume: r[4], vol_ratio: r[5], regime: r[6], atr: r[7]
        }))
        setSymBars(rows)
      }
    } catch {}
    setSymLoading(false)
  }

  // Signals for current symbol to overlay as markers on the chart
  const symSignals = signals.filter(s => s.symbol === symbol.toUpperCase())

  return (
    <div className="space-y-5">

      {/* Signal Optimizer panel removed 2026-08-11 -- was non-functional, see comment near top of file */}

      {/* Filter bar */}
      <div className="rounded-xl border border-gray-800 p-4" style={{ background: '#111827' }}>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 text-slate-600">
            <Filter className="h-3.5 w-3.5" />
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', letterSpacing: '0.1em' }}>
              FILTERS
            </span>
          </div>

          <select value={localDir} onChange={e => setLocalDir(e.target.value)} style={darkSelect}>
            <option value="ALL">ALL DIRECTIONS</option>
            <option value="BUY">BUY ONLY</option>
            <option value="SELL">SELL ONLY</option>
          </select>

          <select value={localHours} onChange={e => setLocalHours(Number(e.target.value))} style={darkSelect}>
            <option value={6}>LAST 6H</option>
            <option value={12}>LAST 12H</option>
            <option value={24}>LAST 24H</option>
            <option value={48}>LAST 48H</option>
          </select>

          <button
            onClick={fetchSignals}
            className="flex items-center gap-1.5 border border-gray-700 rounded-lg px-3 py-1.5 text-slate-500 hover:text-slate-300 hover:bg-gray-800 transition-colors"
            style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem', letterSpacing: '0.08em' }}
          >
            <RefreshCw className="h-3 w-3" />
            REFRESH
          </button>

          {signals.length > 0 && (
            <span
              className="text-slate-700 ml-auto"
              style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem' }}
            >
              {signals.length} signals
            </span>
          )}
        </div>
      </div>

      {error && (
        <div
          className="rounded-lg px-4 py-3 text-sm flex gap-2"
          style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', color: '#fbbf24' }}
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SignalTypeBreakdown data={breakdown} />
        <TopSymbols data={topSyms} />
      </div>

      {/* Signal log table */}
      <div className="rounded-xl border border-gray-800 overflow-hidden" style={{ background: '#111827' }}>
        <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between">
          <h3 className="section-header" style={{ marginBottom: 0 }}>Signal Log</h3>
          {!loading && signals.length > 0 && (
            <span className="text-slate-700" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem' }}>
              {signals.length} rows
            </span>
          )}
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-700">
            <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
            <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>LOADING SIGNALS…</p>
          </div>
        ) : signals.length === 0 ? (
          <div className="p-10 text-center text-slate-700">
            <BarChart2 className="h-10 w-10 mx-auto mb-2 opacity-20" />
            <p className="text-sm text-slate-600">No signals match current filters</p>
            <p className="text-slate-700 mt-1" style={{ fontSize: '0.7rem' }}>Signals are generated during market hours</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: '#0f172a' }}>
                <tr>
                  {['Time', 'Symbol', 'Direction', 'Signal Type', 'Price', 'Strength', 'Conf'].map((h, i) => (
                    <th
                      key={h}
                      className={`px-4 py-3 ${i >= 4 ? 'text-right' : 'text-left'}`}
                      style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem', letterSpacing: '0.1em', color: '#4b5563', fontWeight: 600, textTransform: 'uppercase' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {signals.map((s, i) => {
                  const isBuy = (s.direction||'').toLowerCase() === 'buy'
                  return (
                    <tr
                      key={i}
                      style={{ background: i % 2 === 0 ? '#0f172a' : '#111827' }}
                      className="hover:bg-gray-800 transition-colors"
                    >
                      <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>
                        {timeAgo(s.time)}
                      </td>
                      <td className="px-4 py-2.5 font-semibold text-slate-200" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {s.symbol}
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-bold"
                          style={{
                            fontSize: '0.62rem',
                            letterSpacing: '0.08em',
                            background: isBuy ? 'rgba(16,185,129,0.12)' : 'rgba(244,63,94,0.12)',
                            color:      isBuy ? '#10b981'               : '#f43f5e',
                            border:     isBuy ? '1px solid rgba(16,185,129,0.25)' : '1px solid rgba(244,63,94,0.25)',
                          }}
                        >
                          {isBuy ? <TrendingUp className="h-2.5 w-2.5"/> : <TrendingDown className="h-2.5 w-2.5"/>}
                          {(s.direction||'').toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-500" style={{ fontSize: '0.75rem' }}>
                        {fmtSignalType(s.signal_type)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-slate-300 tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem' }}>
                        {fmtPrice(s.price)}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-10 h-1 rounded-full overflow-hidden" style={{ background: '#1e293b' }}>
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${Math.round(Number(s.strength)*100)}%`,
                                background: 'linear-gradient(90deg, #f59e0b, #10b981)',
                              }}
                            />
                          </div>
                          <span className="text-slate-600 tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', width: '2rem', textAlign: 'right' }}>
                            {Math.round(Number(s.strength)*100)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>
                        {Math.round(Number(s.confidence)*100)}%
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Symbol lookup with TradingView chart */}
      <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
        <h3 className="section-header">Symbol Lookup</h3>

        {/* Search bar */}
        <div className="flex gap-3 mb-5">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-600" />
            <input
              type="text"
              value={symbol}
              onChange={e => setSymbol(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === 'Enter' && lookupSymbol()}
              placeholder="TICKER"
              className="w-full pl-8 pr-3 py-2 rounded-lg text-sm text-slate-200 focus:outline-none placeholder-slate-700"
              style={{
                background: '#0f172a',
                border: '1px solid #374151',
                fontFamily: 'JetBrains Mono, monospace',
                letterSpacing: '0.08em',
              }}
            />
          </div>
          <button
            onClick={lookupSymbol}
            disabled={symLoading}
            className="px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
            style={{
              background: '#1d4ed8',
              color: '#bfdbfe',
              border: '1px solid rgba(59,130,246,0.4)',
              fontFamily: 'JetBrains Mono, monospace',
              letterSpacing: '0.08em',
              fontSize: '0.75rem',
            }}
          >
            {symLoading ? 'LOADING…' : 'LOOK UP'}
          </button>
        </div>

        {/* TradingView price chart */}
        <div className="mb-5 rounded-lg overflow-hidden" style={{ border: '1px solid #1e293b' }}>
          <div className="px-3 py-2 flex items-center justify-between" style={{ background: '#0d1423', borderBottom: '1px solid #1e293b' }}>
            <span className="text-slate-400 font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem', letterSpacing: '0.08em' }}>
              {symbol} — PRICE (1-MIN BARS)
            </span>
            {symBars.length > 0 && symBars[0]?.close && (
              <span className="text-slate-300 tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem' }}>
                {fmtPrice(symBars[0].close)}
              </span>
            )}
          </div>
          <TVChart
            data={symBars}
            signals={symSignals}
            height={220}
            color="#3b82f6"
            showVolume={true}
          />
        </div>

        {/* Indicator table */}
        {symBars.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: '#0f172a' }}>
                <tr>
                  {['Time', 'Close', 'RSI', 'SuperTrend', 'ATR%', 'Regime', 'Vol Ratio'].map((h, i) => (
                    <th
                      key={h}
                      className={`px-3 py-2.5 ${i === 0 || i === 5 ? 'text-left' : 'text-right'}`}
                      style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.6rem', letterSpacing: '0.1em', color: '#4b5563', fontWeight: 600, textTransform: 'uppercase' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {symBars.slice(0, 20).map((b, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#0f172a' : '#111827' }}>
                    <td className="px-3 py-2 text-slate-600" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.68rem' }}>
                      {timeAgo(b.time)}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-300 tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem' }}>
                      {fmtPrice(b.close)}
                    </td>
                    <td
                      className="px-3 py-2 text-right font-medium tabular-nums"
                      style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: '0.75rem',
                        color: b.rsi > 70 ? '#f43f5e' : b.rsi < 30 ? '#10b981' : '#94a3b8',
                      }}
                    >
                      {b.rsi ? Number(b.rsi).toFixed(1) : '—'}
                    </td>
                    <td
                      className="px-3 py-2 text-right font-medium"
                      style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: '0.72rem',
                        color: b.supertrend === 1 ? '#10b981' : b.supertrend === -1 ? '#f43f5e' : '#475569',
                      }}
                    >
                      {b.supertrend === 1 ? '▲ Bull' : b.supertrend === -1 ? '▼ Bear' : '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-500" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.72rem' }}>
                      {b.atr ? `${Number(b.atr).toFixed(2)}%` : '—'}
                    </td>
                    <td className="px-3 py-2 capitalize text-slate-500" style={{ fontSize: '0.72rem' }}>
                      {b.regime || '—'}
                    </td>
                    <td
                      className="px-3 py-2 text-right tabular-nums"
                      style={{
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: '0.72rem',
                        color: Number(b.vol_ratio) >= 1.5 ? '#f59e0b' : '#64748b',
                        fontWeight: Number(b.vol_ratio) >= 1.5 ? 700 : 400,
                      }}
                    >
                      {b.vol_ratio ? Number(b.vol_ratio).toFixed(2) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {symBars.length === 0 && !symLoading && (
          <p className="text-slate-700" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>
            Enter a symbol above and click LOOK UP to see the price chart and indicator data.
          </p>
        )}
      </div>
    </div>
  )
}
